<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import {
  iButton,
  iButtonTypes,
  iButtonStyles,
  iButtonSizes,
  iKeyIcon,
  iKeyIconSize,
  iKeyIconStyle,
  iKeyIconType,
  iKeyIconShape,
  iSpinner,
  showToast,
} from '@inscribe/signal';
import { useConfigurableInsightsStore } from '@/V2/stores/pinia/decision-engine/configurable-insights.store';
import type {
  BacktestResult,
  ConfigurableInsight,
} from '@/types/decision-engine/configurable-insights.types';
import { BacktestStatus } from '@/types/decision-engine/configurable-insights.types';

interface Props {
  insight: ConfigurableInsight;
}

const props = defineProps<Props>();

const store = useConfigurableInsightsStore();

const backtestData = ref<BacktestResult | null>(null);
const showDataLoadingBlocks = ref(false);
const error = ref<string | null>(null);
const POLL_INTERVAL_MS = 1000; // 1 second
const MAX_POLL_ATTEMPTS = 60;

const isBacktestFinished = computed(() => {
  return (
    backtestData.value?.status === BacktestStatus.FINISHED ||
    backtestData.value?.status === BacktestStatus.FAILED ||
    backtestData.value?.status === BacktestStatus.CANCELLED
  );
});

const progressPercentage = computed(() => {
  if (backtestData.value?.progress !== undefined) {
    // progress is in cents (1 = 100%), so multiply by 100 to get percentage
    return Math.min(Math.round(backtestData.value.progress * 100), 100);
  }
  return 0;
});

function formatFlagRate(flagRate: number | undefined): string {
  return flagRate !== undefined && flagRate !== null
    ? (flagRate * 100).toFixed(1) + '%'
    : '-';
}

async function pollUntilFinished(backtestId: string): Promise<BacktestResult> {
  let attempts = 0;

  const poll = async (): Promise<BacktestResult> => {
    const data = await store.getBacktestResults(backtestId);
    backtestData.value = data;

    if (
      data.status === BacktestStatus.FINISHED ||
      data.status === BacktestStatus.FAILED ||
      data.status === BacktestStatus.CANCELLED
    ) {
      return data;
    }

    attempts += 1;
    if (attempts >= MAX_POLL_ATTEMPTS) {
      throw new Error('Backtest job took too long to complete');
    }

    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    return poll();
  };

  return poll();
}

async function createAndPollBacktest() {
  try {
    const response = await store.createBackTestJob(props.insight);
    const backtestId = response.backtest_uuid;
    if (!backtestId) {
      error.value = 'No backtest ID returned from server';
      return;
    }

    await pollUntilFinished(backtestId);

    if (backtestData.value?.status === BacktestStatus.FAILED) {
      error.value = 'Backtest job failed';
    } else if (backtestData.value?.status === BacktestStatus.CANCELLED) {
      error.value = 'Backtest job was cancelled';
    } else {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      showDataLoadingBlocks.value = false;
    }
  } catch (err: unknown) {
    showToast({
      text: err instanceof Error ? err.message : 'Error creating the preview',
      isError: true,
    });
    error.value =
      err instanceof Error ? err.message : 'Error creating the preview';
    showDataLoadingBlocks.value = false;
  }
}

function startLoadingImpact() {
  backtestData.value = null;
  showDataLoadingBlocks.value = true;
  error.value = null;
  createAndPollBacktest();
}

onMounted(async () => {
  if (!props.insight.uuid) return;

  const backtests = await store.getBacktestsForSignal(props.insight.uuid);
  const latest = backtests.find((b) => b.status === BacktestStatus.FINISHED);
  if (latest) {
    backtestData.value = latest;
  }
});
</script>

<template>
  <div class="backtest-preview-container d-flex flex-column i-gap-9">
    <div class="title i-text-heading-6">Summary</div>
    <div class="backtest-preview i-radius-5xl">
      <!-- header -->
      <div
        class="header i-p-11 d-flex flex-row align-items-center justify-content-between i-gap-8"
      >
        <i-key-icon
          icon="medium-solid-alert-triangle"
          :size="iKeyIconSize.XL"
          :type="iKeyIconType.ERROR"
          :icon-style="iKeyIconStyle.SOLID_LIGHT"
          :use-solid-icon-colour="true"
          class="info-icon"
        />
        <div
          class="description d-flex flex-column i-text-body-2 i-gap-1 align-items-start"
        >
          <div class="title i-text-heading-6">
            {{ props.insight.signal_name || 'Insight Name' }}
          </div>
          <div class="subtitle i-text-body-2">
            {{
              props.insight.configuration.description || 'Insight Description'
            }}
          </div>
        </div>
        <i-button
          :type="iButtonTypes.NEUTRAL"
          :button-style="iButtonStyles.SOLID_LIGHT"
          :size="iButtonSizes.MEDIUM"
          label="Estimate Impact"
          @button-click="startLoadingImpact"
          data-dd-action-name="estimate-impact-button"
        ></i-button>
      </div>

      <!-- content -->
      <div class="content d-flex flex-column i-p-11 i-gap-6">
        <div class="title i-text-button-2">Estimated Impact</div>

        <!-- loading block-->
        <Transition name="loading-card-fade">
          <div
            v-if="showDataLoadingBlocks"
            class="loading-card d-flex flex-row align-items-center justify-content-space-between i-px-10 i-py-8 i-gap-6 i-radius-3xl"
          >
            <i-key-icon
              icon="medium_clock_circle"
              :size="iKeyIconSize.MD"
              :type="iKeyIconType.NEUTRAL"
              :shape="iKeyIconShape.SQUARE"
              :icon-style="iKeyIconStyle.SOLID_LIGHT"
              class="loading-block-icon"
            />
            <div class="text d-flex flex-column">
              <div class="title i-text-button-2">Running analysis</div>
              <div class="subtitle i-text-body-2">
                {{
                  error
                    ? error
                    : showDataLoadingBlocks
                      ? `${progressPercentage}% complete`
                      : 'Estimates improving as more data is processed'
                }}
              </div>
            </div>
            <i-spinner v-if="!isBacktestFinished" size="md" />
          </div>
        </Transition>

        <!-- data cards -->
        <div class="card-container d-flex flex-row i-gap-6">
          <div class="card d-flex flex-column i-p-11 i-gap-2 i-radius-3xl">
            <div class="label i-text-body-2">Customers</div>
            <div
              v-if="showDataLoadingBlocks && !backtestData"
              class="loading-block linear-background i-radius-lg"
            ></div>
            <div v-else class="value i-text-heading-3">
              {{ backtestData?.cohort_size ?? '-' }}
            </div>
          </div>
          <div class="card d-flex flex-column i-p-11 i-gap-2 i-radius-3xl">
            <div class="label i-text-body-2">Would be flagged</div>
            <div
              v-if="showDataLoadingBlocks && !backtestData"
              class="loading-block linear-background i-radius-lg"
            ></div>
            <div v-else class="value i-text-heading-3">
              {{ backtestData?.flagged_count ?? '-' }}
            </div>
          </div>
          <div class="card d-flex flex-column i-p-11 i-gap-2 i-radius-3xl">
            <div class="label i-text-body-2">Flag rate</div>
            <div
              v-if="showDataLoadingBlocks && !backtestData"
              class="loading-block linear-background i-radius-lg"
            ></div>
            <div v-else class="value i-text-heading-3">
              {{ formatFlagRate(backtestData?.flag_rate) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.backtest-preview {
  background: var(--base);
  border: 1px solid var(--border-neutral);

  .header {
    border-bottom: 1px solid var(--border-neutral-subtle);
    .description {
      flex: 1;
      .title {
        color: var(--text-neutral-bold);
      }

      .subtitle {
        color: var(--text-neutral);
      }
    }
  }

  .content {
    .title {
      color: var(--text-neutral-bold);
    }

    .loading-card-fade-leave-active {
      transition: opacity 0.15s ease;
    }

    .loading-card-fade-leave-to {
      opacity: 0;
    }

    .loading-card {
      width: 100%;
      background: var(--surface-neutral-subtle);
      .loading-block-icon {
        background: var(--base);
      }
      .text {
        flex: 1;
        .title {
          color: var(--text-neutral-bold);
        }
        .subtitle {
          color: var(--text-neutral);
        }
      }
    }

    .card-container {
      .card {
        flex: 1;
        .label {
          color: var(--text-neutral);
        }

        .value {
          color: var(--text-neutral-bold);
        }
        .loading-block {
          width: 80px;
          height: 20px;
          background: var(--surface-neutral-subtle);
        }
      }
    }
  }
}
</style>
